import React, { useState, useEffect } from "react";
import { MapContainer, TileLayer, Marker, Polygon, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Mangalagiri, Andhra Pradesh — default map center when adding a brand new
// area with no points yet
const MANGALAGIRI_CENTER = [16.4307, 80.5525];
const DEFAULT_ZOOM = 13;

// When editing an existing area, pan/zoom the map so its saved points are
// all in view instead of opening on the default Mangalagiri center. Runs
// once on mount — the modal is remounted (via a `key`) each time it opens
// for a different area, so "on mount" lines up with "on open".
function FitToPoints({ points }) {
    const map = useMap();
    useEffect(() => {
        if (points.length > 0) {
            const bounds = L.latLngBounds(points);
            map.fitBounds(bounds, { padding: [40, 40] });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return null;
}

// Small round dot used for each point of the area outline, labeled with its
// order number (1, 2, 3, 4...) so the fixed placement order stays visible
// even after a point has been dragged somewhere else on the map.
function pointIcon(orderNumber) {
    return L.divIcon({
        className: "area-point-marker",
        html: `<span>${orderNumber}</span>`,
        iconSize: [22, 22],
        iconAnchor: [11, 11],
    });
}

// Perpendicular distance (in pixels) from point p to the segment a-b, and
// the closest point on that segment — used to find the true nearest edge
// to a new click before converting that closest point back to lat/lng.
function closestPointOnSegment(p, a, b) {
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const lenSq = dx * dx + dy * dy;
    if (lenSq === 0) return { x: a.x, y: a.y };
    let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / lenSq;
    t = Math.max(0, Math.min(1, t));
    return { x: a.x + t * dx, y: a.y + t * dy };
}
// Points always connect in the order they were placed — point[0] -> point[1] -> point[2] -> ...
// -> back to point[0] — never re-ordered by which points happen to sit closest together.
// Each dot can also be dragged to a new spot; dragging only moves that dot's
// coordinates, it never changes its position (index) in the sequence, so the
// connection order stays exactly as placed even after moving points around.
function AreaDrawingLayer({ points, setPoints }) {
    const map = useMapEvents({
        click(e) {
            setPoints((prev) => {
                // Fewer than 2 points: no edges exist yet, just append.
                if (prev.length < 2) {
                    return [...prev, [e.latlng.lat, e.latlng.lng]];
                }

                // Edges to check: consecutive pairs, plus the closing edge
                // (last -> first) once the shape has 3+ points.
                const edgeCount = prev.length >= 3 ? prev.length : prev.length - 1;
                const clickPx = map.latLngToContainerPoint(e.latlng);

                let bestEdgeIdx = -1;
                let bestDistMeters = Infinity;
                for (let i = 0; i < edgeCount; i++) {
                    const aLatLng = L.latLng(prev[i][0], prev[i][1]);
                    const bLatLng = L.latLng(prev[(i + 1) % prev.length][0], prev[(i + 1) % prev.length][1]);
                    const aPx = map.latLngToContainerPoint(aLatLng);
                    const bPx = map.latLngToContainerPoint(bLatLng);

                    // Find the closest point on this edge in pixel space, then
                    // convert it back to a real lat/lng and measure the actual
                    // geographic distance from the click — this is what decides
                    // which edge is "nearest", not screen pixels alone.
                    const closestPx = closestPointOnSegment(clickPx, aPx, bPx);
                    const closestLatLng = map.containerPointToLatLng([closestPx.x, closestPx.y]);
                    const distMeters = map.distance(e.latlng, closestLatLng);

                    if (distMeters < bestDistMeters) {
                        bestDistMeters = distMeters;
                        bestEdgeIdx = i;
                    }
                }

                // Always drop the new point into the nearest edge in the
                // sequence, right between that edge's two endpoints — this is
                // what keeps a point placed near the 4-5 edge connected as
                // "...4 -> new -> 5..." instead of tacking it on after the
                // last point and closing all the way back to point 1.
                const next = [...prev];
                next.splice(bestEdgeIdx + 1, 0, [e.latlng.lat, e.latlng.lng]);
                return next;
            });
        },
    });

    const handleDragEnd = (idx, e) => {
        const { lat, lng } = e.target.getLatLng();
        setPoints((prev) => prev.map((pos, i) => (i === idx ? [lat, lng] : pos)));
    };

    return (
        <>
            {points.map((pos, idx) => (
                <Marker
                    key={idx}
                    position={pos}
                    icon={pointIcon(idx + 1)}
                    draggable={true}
                    eventHandlers={{
                        dragend: (e) => handleDragEnd(idx, e),
                        contextmenu: (e) => {
                            e.originalEvent.preventDefault();
                            setPoints((prev) => prev.filter((_, i) => i !== idx));
                        },
                    }}
                />
            ))}
            {points.length > 1 && (
                <Polygon
                    // `positions` is passed in placement order (index 0, 1, 2, 3...),
                    // so Leaflet always draws the edges 1->2->3->4->...->1 in that
                    // exact order — it never picks the geometrically nearest point.
                    positions={points}
                    pathOptions={{ color: "#4f8ff0", weight: 2, fillColor: "#4f8ff0", fillOpacity: 0.15 }}
                />
            )}
        </>
    );
}

function PointOrderList({ points, setPoints }) {
    if (points.length === 0) return null;

    const moveUp = (idx) => {
        if (idx === 0) return;
        setPoints((prev) => {
            const next = [...prev];
            [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
            return next;
        });
    };

    const moveDown = (idx) => {
        setPoints((prev) => {
            if (idx === prev.length - 1) return prev;
            const next = [...prev];
            [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
            return next;
        });
    };

    const removeAt = (idx) => {
        setPoints((prev) => prev.filter((_, i) => i !== idx));
    };

    return (
        <div className="ana-point-list">
            {points.map((pos, idx) => (
                <div className="ana-point-row" key={idx}>
                    <span className="ana-point-num">{idx + 1}</span>
                    <span className="ana-point-coords">
            {pos[0].toFixed(4)}, {pos[1].toFixed(4)}
          </span>
                    <div className="ana-point-controls">
                        <button
                            type="button"
                            className="ana-point-btn"
                            onClick={() => moveUp(idx)}
                            disabled={idx === 0}
                            aria-label="Move point earlier in sequence"
                        >
                            ↑
                        </button>
                        <button
                            type="button"
                            className="ana-point-btn"
                            onClick={() => moveDown(idx)}
                            disabled={idx === points.length - 1}
                            aria-label="Move point later in sequence"
                        >
                            ↓
                        </button>
                        <button
                            type="button"
                            className="ana-point-btn ana-point-btn-danger"
                            onClick={() => removeAt(idx)}
                            aria-label="Delete point"
                        >
                            ×
                        </button>
                    </div>
                </div>
            ))}
        </div>
    );
}

export default function AddNewAreaModal({ open, mode = "add", initialArea, onClose, onSave, onDelete }) {
    const isEdit = mode === "edit";

    // Seeded once per mount. The parent remounts this component (via a
    // changing `key`) whenever it opens for "add" vs. a specific area to
    // edit, so these initializers always start from the right data.
    const [name, setName] = useState(initialArea?.name || "");
    const [description, setDescription] = useState(initialArea?.description || "");
    const [points, setPoints] = useState(
        initialArea?.points ? initialArea.points.map((p) => [p[0], p[1]]) : []
    );
    // Stack of previous point sets, one snapshot per change, so the back
    // button can step backwards through adds, drags, deletes, and reorders.
    const [history, setHistory] = useState([]);

    if (!open) return null;

    // Every point mutation goes through here instead of calling setPoints
    // directly, so a snapshot of the "before" state is always saved first.
    const updatePoints = (updaterOrValue) => {
        const nextPoints = typeof updaterOrValue === "function" ? updaterOrValue(points) : updaterOrValue;
        setHistory((h) => [...h, points]);
        setPoints(nextPoints);
    };

    const handleUndo = () => {
        if (history.length === 0) return;
        const previous = history[history.length - 1];
        setHistory((h) => h.slice(0, -1));
        setPoints(previous);
    };

    const handleClose = () => {
        setName("");
        setDescription("");
        setPoints([]);
        setHistory([]);
        onClose && onClose();
    };

    const handleSave = () => {
        onSave && onSave({ name, description, points });
        handleClose();
    };

    const handleDelete = () => {
        onDelete && onDelete();
        handleClose();
    };

    const canSave = name.trim().length > 0 && points.length >= 3;

    return (
        <div className="ana-overlay" onMouseDown={(e) => e.target === e.currentTarget && handleClose()}>
            <div className="ana-modal">
                <div className="ana-header">
                    <h2>{isEdit ? "Edit Area" : "Add New Area"}</h2>
                    <button className="ana-close" onClick={handleClose} aria-label="Close">
                        ×
                    </button>
                </div>

                <div className="ana-field-row">
                    <label>Name:</label>
                    <input
                        type="text"
                        placeholder="Area name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                    />
                </div>

                <div className="ana-field-row">
                    <label>Description:</label>
                    <input
                        type="text"
                        placeholder="Short description (optional)"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                    />
                </div>

                <div className="ana-map-wrap">
                    <MapContainer
                        center={MANGALAGIRI_CENTER}
                        zoom={DEFAULT_ZOOM}
                        scrollWheelZoom={true}
                        style={{ height: "420px", width: "100%" }}
                    >
                        <TileLayer
                            attribution="&copy; OpenStreetMap contributors"
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                        <AreaDrawingLayer points={points} setPoints={updatePoints} />
                        <FitToPoints points={points} />
                    </MapContainer>
                </div>

                <div className="ana-hint">
                    <span className="ana-hint-icon">!</span>
                    <span>
            {isEdit
                ? "Existing points are loaded below — drag, delete, or add more to update the area."
                : "Each new click is inserted at its nearest edge in the outline automatically."}
                        {" "}Drag a dot to reposition it, right click to delete it, or fix the order manually below.
          </span>
                </div>

                <PointOrderList points={points} setPoints={updatePoints} />

                <div className="ana-footer">
                    <button
                        className="ana-btn ana-btn-ghost"
                        onClick={handleUndo}
                        disabled={history.length === 0}
                        aria-label="Undo last point change"
                    >
                        <span className="ana-btn-icon">←</span> Back
                    </button>
                    <div className="ana-footer-right">
                        <button className="ana-btn ana-btn-ghost" onClick={() => updatePoints([])} disabled={points.length === 0}>
                            Clear points
                        </button>
                        {isEdit && (
                            <button className="ana-btn ana-btn-danger-outline" onClick={handleDelete}>
                                Delete area
                            </button>
                        )}
                        <button className="ana-btn ana-btn-primary" onClick={handleSave} disabled={!canSave}>
                            {isEdit ? "Save changes" : "Save area"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}